<template>
  <div v-if="showIndicator" class="download-limit-indicator">
    <!-- Authenticated User - Unlimited Downloads -->
    <div v-if="downloadInfo.isAuthenticated" class="authenticated-info">
      <div class="flex items-center space-x-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
        <svg class="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p class="text-sm text-green-800 dark:text-green-200">
          Unlimited downloads available
        </p>
      </div>
    </div>

    <!-- Anonymous User - Unified Download Status -->
    <div v-else class="anonymous-download-status">
      <!-- Main Status Card -->
      <div :class="[
        'flex items-center justify-between p-3 rounded-lg border transition-all duration-300',
        hasDownloadsRemaining 
          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
          : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
      ]">
        <div class="flex items-center space-x-2">
          <!-- Dynamic Icon -->
          <svg :class="[
            'w-5 h-5',
            hasDownloadsRemaining ? 'text-blue-500' : 'text-red-500'
          ]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path v-if="hasDownloadsRemaining" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
          </svg>
          
          <!-- Dynamic Content -->
          <div>
            <p :class="[
              'text-sm font-medium',
              hasDownloadsRemaining 
                ? 'text-blue-900 dark:text-blue-100' 
                : 'text-red-900 dark:text-red-100'
            ]">
              {{ statusMessage }}
            </p>
            <p :class="[
              'text-xs',
              hasDownloadsRemaining 
                ? 'text-blue-700 dark:text-blue-300' 
                : 'text-red-700 dark:text-red-300'
            ]">
              {{ subMessage }}
            </p>
          </div>
        </div>
        
        <!-- Dynamic Sign Up Button -->
        <button 
          @click="$emit('create-account')"
          :class="[
            'px-3 py-1.5 text-white text-xs font-medium rounded-md transition-colors',
            hasDownloadsRemaining 
              ? 'bg-blue-500 hover:bg-blue-600' 
              : 'bg-red-500 hover:bg-red-600'
          ]"
        >
          {{ buttonText }}
        </button>
      </div>
      
      <!-- Progress Bar -->
      <div class="mt-2">
        <div class="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
          <span>Free Downloads Used</span>
          <span>{{ usedDownloads }}/{{ downloadInfo.total }}</span>
        </div>
        <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div 
            :class="[
              'h-2 rounded-full transition-all duration-300',
              hasDownloadsRemaining ? 'bg-blue-500' : 'bg-red-500'
            ]"
            :style="{ width: `${progressPercentage}%` }"
          ></div>
        </div>
      </div>

      <!-- Warning for Last Download -->
      <div v-if="downloadInfo.remaining === 1" 
           class="mt-2 p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
        <div class="flex items-center space-x-2">
          <svg class="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <p class="text-sm text-orange-800 dark:text-orange-200">
            This is your last free download today!
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { centralizedImageSecurity } from '@/services/centralizedImageSecurity';

interface Props {
  showIndicator?: boolean;
  compact?: boolean;
}

interface Emits {
  (e: 'create-account'): void;
  (e: 'download-limit-reached'): void;
}

withDefaults(defineProps<Props>(), {
  showIndicator: true,
  compact: false
});

const emit = defineEmits<Emits>();

// Reactive state
const downloadInfo = ref({
  remaining: 3,
  total: 3,
  isAuthenticated: false
});

// Computed properties
const usedDownloads = computed(() => {
  return downloadInfo.value.total - downloadInfo.value.remaining;
});

const progressPercentage = computed(() => {
  if (downloadInfo.value.total === 0) return 0;
  return (usedDownloads.value / downloadInfo.value.total) * 100;
});

const hasDownloadsRemaining = computed(() => {
  return downloadInfo.value.remaining > 0;
});

const statusMessage = computed(() => {
  if (hasDownloadsRemaining.value) {
    return `${downloadInfo.value.remaining} free download${downloadInfo.value.remaining === 1 ? '' : 's'} remaining today`;
  } else {
    return 'No free downloads remaining';
  }
});

const subMessage = computed(() => {
  if (hasDownloadsRemaining.value) {
    return 'Create an account for unlimited downloads';
  } else {
    return 'Create an account to continue downloading';
  }
});

const buttonText = computed(() => {
  return hasDownloadsRemaining.value ? 'Sign Up' : 'Sign Up Now';
});

// Methods
const updateDownloadInfo = () => {
  downloadInfo.value = centralizedImageSecurity.getRemainingDownloads();
  
  // Emit event if no downloads remaining
  if (!downloadInfo.value.isAuthenticated && downloadInfo.value.remaining === 0) {
    emit('download-limit-reached');
  }
};

// Lifecycle
onMounted(() => {
  updateDownloadInfo();
  
  // Update download info every 30 seconds to catch changes
  setInterval(updateDownloadInfo, 30000);
});

// Expose method for parent components to refresh the info
defineExpose({
  refresh: updateDownloadInfo
});
</script>

<style scoped>
.download-limit-indicator {
  @apply w-full;
}

.free-downloads-info {
  @apply space-y-2;
}

.authenticated-info {
  @apply opacity-75;
}

/* Compact mode styles */
.compact .free-downloads-info {
  @apply space-y-1;
}

.compact .free-downloads-info .flex {
  @apply p-2;
}

.compact .free-downloads-info p {
  @apply text-xs;
}

.compact button {
  @apply px-2 py-1 text-xs;
}

/* Animation for progress bar */
.bg-blue-500 {
  transition: width 0.3s ease-in-out;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .download-limit-indicator {
    @apply text-sm;
  }
  
  .download-limit-indicator button {
    @apply px-2 py-1 text-xs;
  }
}
</style>